import {
  autoUpdate,
  FloatingNode,
  FloatingPortal,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { SlidersHorizontalIcon } from '@phosphor-icons/react'
import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'

import { useViewSettingsInline } from './use-view-settings-inline'
import * as S from './view-settings.styled'

interface ViewSettingsProps {
  children: ReactNode
}

// Note: a floating island of view settings, always visible on a wide screen. Render it as the last
// child of LinkedColumns; below that width it renders nothing - use ViewSettingsMenu in the page
// header with the same children
export const ViewSettings = ({ children }: ViewSettingsProps) => {
  const { t } = useTranslation('common')
  const isInline = useViewSettingsInline()

  if (!isInline) return null

  return (
    <S.Island aria-label={t('linkedColumns.settings')}>
      <Typography as="h2" color="black800" size="heading-tiny">
        {t('linkedColumns.settings')}
      </Typography>
      <S.Content>{children}</S.Content>
    </S.Island>
  )
}

// Note: the same view settings in a popover opened from a page header button, for screens too narrow
// for the island. Renders nothing on a wide screen
export const ViewSettingsMenu = ({ children }: ViewSettingsProps) => {
  const { t } = useTranslation('common')
  const isInline = useViewSettingsInline()
  const [isOpen, setIsOpen] = useState(false)
  // Note: a node in the app's FloatingTree, so the selects and the color picker inside count as its
  // children - pressing in them or Escape closes only them, not the whole menu
  const nodeId = useFloatingNodeId()

  const { refs, floatingStyles, context } = useFloating({
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    nodeId,
    onOpenChange: setIsOpen,
    open: isOpen,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context),
    useRole(context, { role: 'dialog' }),
  ])

  if (isInline) return null

  return (
    <>
      <S.Anchor ref={refs.setReference} {...getReferenceProps()}>
        <Button active={isOpen} size="small" use="secondary">
          <Icon icon={SlidersHorizontalIcon} />
          {t('linkedColumns.settings')}
        </Button>
      </S.Anchor>

      <FloatingNode id={nodeId}>
        {isOpen && (
          <FloatingPortal>
            <S.Popover
              ref={refs.setFloating}
              aria-label={t('linkedColumns.settings')}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              {children}
            </S.Popover>
          </FloatingPortal>
        )}
      </FloatingNode>
    </>
  )
}
